import React from 'react';
import {
  BirdzDialog,
  BirdzModalTitle,
  BirdzNotif,
  useDialog,
  useNotif
} from '@applications-terrains/birdz-react-library';
import { useEffect, useState } from 'react';
import ModalWithLoader, {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { displayClientsOrContracts, translate } from './utils';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Edit } from '@mui/icons-material';
import axios from 'axios';
import BTable from '../../../Tools/Table/BTable';
import TrueFalse from '../../../Tools/TrueFalse';
import { AddCircle } from '@mui/icons-material';
import { ExocetFamilyType, ExocetRadioModeType } from '../Types';
import DefaultConfigurationForm from './DefaultConfigurationForm';
import { DefaultRadioConfig, RadioConfig } from './types';
import { FullSizeLoader } from '../../../Tools/Suspended';
import { capitalizeFirstLetter } from '../../../../utils';
import useRefresh from '../../../../hooks/useRefresh';
import { Client } from '../../../DndComponents/ContractsDnd/types';

const DefaultConfigTable = ({
  filterString,
  moduleFamilies,
  radioModes,
  createDefaultConfig,
  setCreateDefaultConfig,
  editDefaultConfig,
  setEditDefaultConfig,
  configurations,
  defaultConfigurations,
  triggerRefreshFromParent,
  refreshFromParent,
  clients = []
}: {
  filterString: string;
  moduleFamilies?: ExocetFamilyType[];
  radioModes?: ExocetRadioModeType[];
  createDefaultConfig: RadioConfig | null;
  setCreateDefaultConfig: (value: RadioConfig | null) => void;
  editDefaultConfig: DefaultRadioConfig | null;
  setEditDefaultConfig: (value: DefaultRadioConfig | null) => void;
  configurations: RadioConfig[];
  defaultConfigurations: DefaultRadioConfig[];
  triggerRefreshFromParent: () => void;
  refreshFromParent: boolean;
  clients?: Client[];
}) => {
  const { refresh, triggerRefresh } = useRefresh();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const { notif, notifOptions } = useNotif();
  const [action, setAction] = useState<ModalWithLoaderAction>(null);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);
  const [selectedDefaultConfig, setSelectedDefaultConfig] = useState<
    DefaultRadioConfig | 'new' | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { t } = useTranslation();

  // Demande d'édition venue du profil radio : on alimente le flux d'édition
  // existant, puis on la consomme pour qu'une fermeture ne la rejoue pas.
  useEffect(() => {
    if (!editDefaultConfig) return;
    setAction('edit');
    setSelectedDefaultConfig(editDefaultConfig);
    setEditDefaultConfig(null);
  }, [editDefaultConfig, setEditDefaultConfig]);

  // Les écritures de cette table doivent aussi invalider les listes complètes
  // chargées par le parent, dont dépend la cascade familles <-> config par défaut.
  const refreshAll = () => {
    triggerRefresh();
    setTimeout(() => {
      triggerRefreshFromParent();
    }, 0);
  };

  const formatFilterString = (filterString: string, param: string) =>
    '&' +
    filterString
      .split('&')
      .filter(Boolean)
      .map((key) => param + key)
      .join('&') +
    '&';

  const actions = [
    // {
    //   name: 'duplicate',
    //   label: 'Dupliquer la configuration par défaut',
    //   render: (item: DefaultRadioConfig) => {
    //     return (
    //       <IconButton
    //         onClick={() => {
    //           setAction('duplicate');
    //           setSelectedDefaultConfig(item);
    //         }}
    //       >
    //         <ContentCopy fontSize="small" />
    //       </IconButton>
    //     );
    //   }
    // },
    {
      name: 'edit',
      label: 'Modifier la configuration par défaut',
      render: (item: DefaultRadioConfig) => {
        return (
          <IconButton
            onClick={() => {
              setAction('edit');
              setSelectedDefaultConfig(item);
            }}
          >
            <Edit fontSize="small" color="action" />
          </IconButton>
        );
      }
    },
    {
      name: 'delete',
      label: t('equipmentConfiguration.deleteException'),
      render: (param: any) => {
        return (
          <IconButton
            onClick={() => {
              confirmDialog({
                title: t('equipmentConfiguration.deleteExceptionTitle'),
                content: t('equipmentConfiguration.deleteExceptionContent'),
                onValidate: () => {
                  axios.delete(`/api/boi/params/default-configurations/${param.id}/`).then(
                    () => {
                      refreshAll();
                      notif({
                        content: t('equipmentConfiguration.deleteExceptionSuccess'),
                        type: 'success'
                      });
                    },
                    () => {
                      notif({
                        content: t('equipmentConfiguration.deleteError'),
                        type: 'error'
                      });
                    }
                  );
                  closeDialog();
                },
                onCancel: () => {
                  closeDialog();
                }
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      }
    }
  ];

  const handleCloseModalWithLoader = () => {
    setAction(null);
    setRequestStatus(null);
    setSelectedDefaultConfig(null);
  };

  return (
    <>
      <Typography variant="overline" ml={4} mt={4}>
        {t('equipmentConfiguration.defaultConfigurations')}
      </Typography>

      <BTable
        model="default-configurations"
        endpoint={
          `/api/boi/params/default-configurations/` + formatFilterString(filterString, 'profile__')
        }
        refresh={refreshFromParent || refresh}
        defaultOrder={'profile_name'}
        fields={[
          {
            name: 'profile_name',
            label: t('sidebar.configuration'),
            type: 'text'
          },
          {
            name: 'profile_radio_mode',
            label: t('equipmentConfiguration.radioMode'),
            transform: (value) => {
              const radioMode =
                radioModes?.find((mode: ExocetRadioModeType) => value === mode.Id)?.Label ?? value;
              return radioMode;
            }
          },
          {
            name: 'families',
            label: t('equipmentConfiguration.equipmentFamilies'),
            type: 'text',
            transform: (values) => {
              const familiesName: string[] = [];
              if (values?.length === moduleFamilies?.length) {
                return t('equipmentConfiguration.all');
              } else
                moduleFamilies?.map((family) => {
                  values?.forEach((value: number) => {
                    if (value === family.Id) {
                      const name = translate(family.Name);
                      familiesName.push(name);
                    }
                  });
                });
              return familiesName.join(', ');
            }
          },
          {
            name: 'profile_hourly_index',
            label: t('equipmentConfiguration.hourly_index'),
            type: 'text',
            transform: (value: boolean) => {
              return <TrueFalse value={value} />;
            }
          },
          {
            name: 'clients',
            label: t('equipmentConfiguration.clients'),
            transform: (_, row) => {
              return row.clients?.length === 0 && row.contracts?.length === 0
                ? t('equipmentConfiguration.all')
                : displayClientsOrContracts(row.clients, clients);
            }
          },
          {
            name: 'contracts',
            label: t('equipmentConfiguration.contracts'),
            transform: (values) => displayClientsOrContracts(values, clients)
          }
        ]}
        actions={actions}
        headerActions={[
          {
            name: t('equipmentConfiguration.addException'),
            element: (
              <IconButton
                onClick={() => {
                  setAction('add');
                  setSelectedDefaultConfig('new');
                }}
              >
                <AddCircle fontSize="small" color="action" />
              </IconButton>
            )
          }
        ]}
      />

      <ModalWithLoader
        openModal={
          createDefaultConfig !== null ||
          action === 'add' ||
          action === 'edit' ||
          action === 'duplicate'
        }
        onClose={() => handleCloseModalWithLoader()}
        onSuccess={() => handleCloseModalWithLoader()}
        action={
          action === 'delete'
            ? t('equipmentConfiguration.deleteExceptionAction')
            : action === 'edit'
              ? t('equipmentConfiguration.editExceptionAction')
              : t('equipmentConfiguration.createExceptionAction')
        }
        status={requestStatus}
        setStatus={setRequestStatus}
        maxWidth="xl"
        customErrorMessage={errorMessage}
        timeout={1500}
        header={
          <BirdzModalTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {capitalizeFirstLetter(
                `${action === 'edit' ? t('edit') + ' ' + t('equipmentConfiguration.theDefaultConfiguration') + ' ' : t('add') + ' ' + t('equipmentConfiguration.aDefaultConfiguration')}`
              )}
              {action === 'edit' && (
                <Box fontWeight={'400'} color={'rgba(0, 0, 0, 0.87)'} ml={1}>
                  {createDefaultConfig
                    ? createDefaultConfig.name
                    : selectedDefaultConfig && selectedDefaultConfig !== 'new'
                      ? selectedDefaultConfig.profile_name
                      : ''}
                </Box>
              )}
            </Box>
          </BirdzModalTitle>
        }
      >
        {createDefaultConfig || selectedDefaultConfig ? (
          <DefaultConfigurationForm
            triggerRefresh={refreshAll}
            configurations={configurations}
            defaultConfigurations={defaultConfigurations}
            setErrorMessage={setErrorMessage}
            setRequestStatus={setRequestStatus}
            action={createDefaultConfig ? 'createDefault' : action}
            setAction={setAction}
            config={
              createDefaultConfig
                ? {
                    ...createDefaultConfig,
                    profile: createDefaultConfig.id,
                    profile_name: createDefaultConfig.name,
                    profile_dataset_code: createDefaultConfig.dataset_code,
                    profile_radio_mode: createDefaultConfig.radio_mode,
                    profile_hourly_index: createDefaultConfig.hourly_index ? 'True' : 'False',
                    families: createDefaultConfig.families
                  }
                : selectedDefaultConfig === 'new'
                  ? null
                  : selectedDefaultConfig
            }
            moduleFamilies={moduleFamilies}
            radioModes={radioModes}
            customers={clients}
            onClose={() => {
              setSelectedDefaultConfig(null);
              setAction(null);
              setCreateDefaultConfig(null);
            }}
          />
        ) : (
          <FullSizeLoader fullSizeSkeleton />
        )}
      </ModalWithLoader>
      <BirdzDialog options={dialogOptions} />
      <BirdzNotif options={notifOptions} />
    </>
  );
};

export default DefaultConfigTable;
